import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useEffect, useState } from "react";
import {
  ActionItemSummaryDto,
  GetPlanDto,
  queryKeys,
  useGetPlan,
  useGetPlanSubitems,
  type QueryParams,
} from "src/http";
import { IPlan } from "src/models";

const useGetPlanTree = (
  planGuid: string,
  dateTimeLocal?: string,
  dateTimeLocalUtcOffsetInMinutes?: number,
  planQueryParams?: QueryParams,
  planSubitemsQueryParams?: QueryParams,
) => {
  const { data, isLoading } = useGetPlan(planGuid, dateTimeLocal, dateTimeLocalUtcOffsetInMinutes, planQueryParams);
  const { data: subitemsData, fetchNextPage } = useGetPlanSubitems(planGuid, planSubitemsQueryParams);
  const [plan, setPlan] = useState<IPlan>();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (data !== undefined) {
      setPlan(data);
    }
  }, [data, subitemsData]);

  async function fetchActionItemSubitems(actionItemGuid: string) {
    const cachedPlan = queryClient.getQueryData<GetPlanDto>(
      queryKeys.plans.getPlanWithOptions(planGuid, dateTimeLocal, dateTimeLocalUtcOffsetInMinutes, planQueryParams),
    );

    if (cachedPlan === undefined) {
      return;
    }

    let subitems: ActionItemSummaryDto[] = [];

    // If subitems have already been cached, get them from the cache.
    if (subitemsData?.pageParams.some((pageParam) => pageParam === actionItemGuid)) {
      subitems = getActionItemSubitemsFromCache(actionItemGuid);
    } else {
      const fetchResult = await fetchNextPage({ pageParam: actionItemGuid });

      if (fetchResult.data?.pageParams.some((pageParam) => pageParam === actionItemGuid)) {
        const pageIndex = fetchResult.data.pageParams.indexOf(actionItemGuid);
        subitems = fetchResult.data.pages[pageIndex];
      }
    }

    const updatedCachedPlan = produce(cachedPlan, (draft) => {
      const actionItems =
        draft.blocks.flatMap((block) => block.sections.flatMap((section) => section.actionItems)) ?? [];
      const matchingActionItems = searchActionItems(actionItems, (actionItem) => actionItem.guid === actionItemGuid);

      matchingActionItems?.forEach((actionItem) => {
        actionItem.subitems = subitems;
      });
    });

    queryClient.setQueryData(
      queryKeys.plans.getPlanWithOptions(planGuid, dateTimeLocal, dateTimeLocalUtcOffsetInMinutes, planQueryParams),
      updatedCachedPlan,
    );
  }

  function getActionItemSubitemsFromCache(actionItemGuid: string) {
    if (subitemsData === undefined) {
      return [];
    }

    const pageIndex = subitemsData.pageParams.indexOf(actionItemGuid);
    if (pageIndex === -1) {
      return [];
    }

    const subitems = subitemsData.pages[pageIndex];

    // Check to see if the subitem has any of its subitems already cached.
    const subitemsWithSubitems = produce(subitems, (draft) => {
      draft.forEach((subitem) => {
        subitem.subitems = getActionItemSubitemsFromCache(subitem.guid);
      });
    });

    return subitemsWithSubitems;
  }

  return {
    plan,
    setPlan,
    isLoading,
    fetchActionItemSubitems,
  };
};

const searchActionItems = (
  actionItems: ActionItemSummaryDto[],
  predicate: (actionItem: ActionItemSummaryDto) => boolean,
): ActionItemSummaryDto[] | undefined => {
  const results: ActionItemSummaryDto[] = [];

  for (let i = 0; i < actionItems.length; i++) {
    const item = actionItems[i];

    if (predicate(item)) {
      results.push(item);
    }

    if (item.subitems.length > 0) {
      const subitemResults = searchActionItems(item.subitems, predicate);

      if (subitemResults) {
        results.push(...subitemResults);
      }
    }
  }

  if (results.length > 0) {
    return results;
  } else {
    return undefined;
  }
};

export { useGetPlanTree };
